import React, { useEffect } from 'react';

import { Divider, Stack } from '@mui/material';

import {
  selectRouteMetrics,
  selectVisualize,
  setRouteMetrics,
  setVisualize,
} from 'state/detourSlices';
import { useAppDispatch, useAppSelector } from 'state/hooks';
import { selectUser } from 'state/userSlice';
import {
  selectCurrentProjectInfo,
  selectTimelineMetric,
  setTimelineMetric,
} from 'state/workflowSlice';

import { SavedRoutesToolbar } from 'features/bookmarks/SavedRoutesToolbar';
import { StlSwitchWithLabel } from 'features/common/StlSwitch/StlSwitch';
import { ToggleGroup } from 'features/common/ToggleGroup';
import { OverflowLabel } from 'features/task_bar/OverflowLabel';

import {
  TL_METRIC_DTR_VOL,
  TL_METRIC_FF_REL_SPEED_VOL,
  TL_METRIC_TYP_REL_SPEED_VOL,
  TL_METRIC_TYP_REL_VOL,
} from '../metricsOptions';

import styles from '../../task_bar/Taskbar.module.css';
import dStyles from './DetourTaskBar.module.css';

export const possibleMetricNames = {
  [TL_METRIC_DTR_VOL]: 'Detoured Volumes',
  [TL_METRIC_TYP_REL_VOL]: 'Atypical Volumes',
  [TL_METRIC_FF_REL_SPEED_VOL]: 'Speeds',
  [TL_METRIC_TYP_REL_SPEED_VOL]: 'Atypical Speeds',
};

export function DetourMetricsOption() {
  const userProject = useAppSelector(selectCurrentProjectInfo);
  const timelineMetric = useAppSelector(selectTimelineMetric);
  const visualize = useAppSelector(selectVisualize);
  const routeMetrics = useAppSelector(selectRouteMetrics);
  const user = useAppSelector(selectUser);
  const {has_hidden_features} = user;
  const dispatch = useAppDispatch();

  const baseMetrics = [TL_METRIC_DTR_VOL, TL_METRIC_TYP_REL_VOL];

  const availableMetrics = has_hidden_features
    ? [...baseMetrics, TL_METRIC_FF_REL_SPEED_VOL, TL_METRIC_TYP_REL_SPEED_VOL]
    : baseMetrics;

  const availableMetricsLabels = Object.fromEntries(
    availableMetrics.map((v) => [v, possibleMetricNames[v]]),
  );
  const safeDefault = TL_METRIC_DTR_VOL;

  const setMetric = (value) => {
    dispatch(setTimelineMetric(value));
  };

  const handleMetricToggleClick = (event) => {
    const newMetricToggleValue = event.target.value;
    setMetric(newMetricToggleValue);
  };

  useEffect(() => {
    if (!availableMetrics.includes(timelineMetric)) {
      dispatch(setTimelineMetric(safeDefault));
    }
  }, [dispatch, timelineMetric, userProject]);

  const handleVisualizeChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const shouldVisualize = event.target.checked;
    dispatch(setVisualize(shouldVisualize));
    if (!shouldVisualize) dispatch(setRouteMetrics(false));
  };

  const handleRouteChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const shouldVisualizeRoutes = event.target.checked;
    dispatch(setRouteMetrics(shouldVisualizeRoutes));
    if (shouldVisualizeRoutes) dispatch(setVisualize(true));
  };

  const mainMetricSelector = (
    <>
      <Stack className={dStyles.metrics_option_stack}>
        <OverflowLabel id="timeline-metric-picker-label">
          <StlSwitchWithLabel
            label="Visualize Metrics"
            checked={visualize}
            onChange={handleVisualizeChange}
          />
          {visualize && (
            <ToggleGroup
              value={timelineMetric}
              values={availableMetrics}
              labelMap={availableMetricsLabels}
              handleClick={handleMetricToggleClick}
              className={styles.toggle_group_metrics}
            />
          )}
        </OverflowLabel>
      </Stack>
      {user.has_hidden_features && (
        <>
          <Divider />
          <Stack className={dStyles.metrics_option_stack}>
            <OverflowLabel id="timeline-route-picker-label">
              <StlSwitchWithLabel
                label="Route Metrics"
                checked={routeMetrics}
                onChange={handleRouteChange}
              />
            </OverflowLabel>
            {routeMetrics && <SavedRoutesToolbar modern />}
          </Stack>
        </>
      )}
    </>
  );

  return <div className={styles.task_bar_component}>{mainMetricSelector}</div>;
}
